/**
 * Narrowing helper for the `chain.createEntry` gateway route's return type.
 *
 * WP-577 / TEN-2918: the route's declared return type is an honest union that includes
 * the preview branch (convex/agentKnowledge/entries.ts + createEntryPreview.ts).
 * tools/smart-capture.ts has two call sites that need this discriminant —
 * `handleCapture`'s single-entry path and `handleBatchCapture`'s per-entry loop — this
 * is the one shared predicate both use, instead of each hand-rolling its own inline
 * `"preview" in result && result.preview` copy.
 *
 * `packages/mcp-server` may not import from `convex/` (scripts/check-mcp-boundaries.mjs),
 * so this is a local structural copy of convex/agentKnowledge/createEntryPreview.ts's
 * `isCreateEntryPreview` — the same duplication packages/cli/src/lib/createEntryResult.ts
 * carries for the same reason. Deliberately tiny and behaviour-free so the duplication
 * cannot drift meaningfully — the discriminant is the presence (and truthiness) of the
 * `preview` key, which is part of the route's public response contract.
 *
 * A plain inline `'preview' in result` check does not reliably narrow the ELSE branch
 * for this generated union shape — an explicit predicate function does (the same
 * limitation is documented in convex/agentKnowledge/createEntryPreview.ts and
 * packages/cli/src/lib/createEntryResult.ts).
 */
import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";

export type CreateEntryRouteResult = GatewayRouteReturnByName<"chain.createEntry">;

export type CreateEntryPreviewRouteResult = Extract<CreateEntryRouteResult, { preview: true }>;

/** True when the `chain.createEntry` route returned its preview branch. */
export function isPreviewCreateEntryResult(
  result: CreateEntryRouteResult,
): result is CreateEntryPreviewRouteResult {
  return "preview" in result && result.preview === true;
}

/**
 * WP-577 review follow-up (P2, TEN-2918): the preview union member carries
 * `wouldBlock`/`blockingReason` (CreateEntryPreviewMutationResult,
 * convex/agentKnowledge/createEntryPreview.ts:63-80). Callers must read this BEFORE
 * casting the union down to its real-create member (which discards the preview member
 * entirely) — otherwise a batch preview item the server says would be BLOCKED (e.g. a
 * governed capture missing its required WHY) gets silently folded into `wouldCapture`
 * instead of reported honestly. Returns undefined outside a preview call, or when the
 * route didn't return its preview branch (a real create — nothing to extract).
 */
export function extractPreviewWouldBlock(
  result: CreateEntryRouteResult | undefined,
  preview: boolean | undefined,
): { wouldBlock: boolean; blockingReason?: string } | undefined {
  return preview && result && isPreviewCreateEntryResult(result)
    ? { wouldBlock: result.wouldBlock === true, blockingReason: result.blockingReason }
    : undefined;
}

/**
 * P0 review round 4 (fix 1/3, root): resolves the EFFECTIVE (post-routing) collection a
 * `chain.createEntry` preview call reports — `createEntryPreview.ts`'s `collection` field on
 * `CreateEntryPreviewMutationResult`, which `prepareCreateEntry` sets to the collectionSlug
 * AFTER `resolveCreateEntryRouting`'s redirect (e.g. a caller-supplied `collectionSlug: "chains"`
 * for a bet-shaped payload routes to "work-packages"). Both of `smart-capture.ts`'s preview call
 * sites — `handleCapture`'s single path and `handleBatchCapture`'s per-entry loop — need this
 * instead of echoing the caller's pre-routing `collectionSlug` in their preview receipts; the
 * batch loop additionally feeds it to `reconcileBatchPreviewDuplicate`
 * (lib/batchPreviewDuplicates.ts, fix 2/3) to catch a later item routing into an earlier item's
 * destination. Falls back to `fallbackCollection` outside a preview call, when the route didn't
 * return its preview branch, or (older/absent field) when the preview branch itself somehow
 * lacks `collection` — additive, never re-derives routing client-side.
 */
export function resolveEffectiveCollection(
  result: CreateEntryRouteResult | undefined,
  preview: boolean | undefined,
  fallbackCollection: string,
): string {
  return preview && result && isPreviewCreateEntryResult(result)
    ? result.collection ?? fallbackCollection
    : fallbackCollection;
}
